import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, cn } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReplenishmentClient, replenishmentKeys, type NegativeFix, type NegativeStock } from "../../../api/replenishment";
import { formatQuantity, formatWhole } from "./shared";
import { OpenRecord } from "../../../components/OpenRecord";

/**
 * Parts below zero where they are kept or used — each one a position the run
 * cannot trust, since "on hand" is part of it.
 *
 * <p>Bought materials come first: a negative there makes the run order too
 * much, or cover a hole that is only on paper. Parts made in house are listed
 * too, but nearly all of those are SIGM never posting production output, a
 * cleanup the Reviews page walks through in full.</p>
 */

const FIXES: readonly NegativeFix[] = ["TRANSFER", "COUNT", "POLICY", "OUTPUT"];

export function NegativesTab() {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const [fix, setFix] = useState<NegativeFix | "BOUGHT">("BOUGHT");
  const negatives = useQuery({ queryKey: replenishmentKeys.negatives, queryFn: () => client.negatives() });

  const all = negatives.data ?? [];
  const counts = new Map<NegativeFix, number>();
  for (const row of all) counts.set(row.fix, (counts.get(row.fix) ?? 0) + 1);
  const bought = all.filter((row) => row.fix !== "OUTPUT").length;
  const rows = fix === "BOUGHT" ? all.filter((row) => row.fix !== "OUTPUT") : all.filter((row) => row.fix === fix);

  const explain: Record<NegativeFix, string> = {
    TRANSFER: t("replenishment.negatives.fix.transfer", "Another warehouse has it: used here, booked there. Transfer it."),
    COUNT: t("replenishment.negatives.fix.count", "Bought and received before: count it and adjust."),
    POLICY: t("replenishment.negatives.fix.policy", "Never received, never ordered: probably made here or an expense. Fix its type or policy."),
    OUTPUT: t("replenishment.negatives.fix.output", "Made here: its output was never posted (SIGM). Post the output or count it."),
  };

  const cols = useMemo(() => {
    const c = defineColumns<NegativeStock>();
    return [
      c.text("part_number", (row) => row.part_number, { headerKey: "column.part.number", priority: 1 }),
      c.text("part_name", (row) => row.part_name, { headerKey: "column.part.name", priority: 2 }),
      c.text("warehouse_code", (row) => row.warehouse_code, { headerKey: "column.stock.warehouse", filter: "select", priority: 2 }),
      c.custom("quantity", (row) => row.quantity, (row) => <span className="tabular-nums">{formatQuantity(row.quantity)}</span>, {
        headerKey: "column.stock.quantity",
        filter: "range",
        align: "right",
        priority: 1,
      }),
      c.text("stock_unit_code", (row) => row.stock_unit_code ?? "", { headerKey: "column.part.unit", filter: "none" }),
      c.text(
        "elsewhere",
        (row) => row.elsewhere.map((other) => `${other.warehouse_code} ${formatQuantity(other.quantity)}`).join(", "),
        { headerKey: "replenishment.negatives.elsewhere", filter: "none" },
      ),
      c.text("last_movement_date", (row) => row.last_movement_date ?? "", { headerKey: "replenishment.negatives.last-move", filter: "daterange" }),
    ];
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="grid grid-cols-2 gap-2 border-b border-line-soft bg-surface px-4 py-3 sm:grid-cols-5 lg:px-6">
        <FixTile
          active={fix === "BOUGHT"}
          label={t("replenishment.negatives.bought", "Bought materials")}
          count={bought}
          onClick={() => setFix("BOUGHT")}
        />
        {FIXES.map((value) => (
          <FixTile
            key={value}
            active={fix === value}
            label={t(`replenishment.negatives.label.${value.toLowerCase()}`, value)}
            count={counts.get(value) ?? 0}
            onClick={() => setFix(value)}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-[12px] text-ink-muted lg:px-6">
        <span>{fix === "BOUGHT" ? t("replenishment.negatives.bought.hint", "These distort the run: each one's position is off by its negative.") : explain[fix]}</span>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => void navigate({ to: "/purchasing/reviews" })}>
          {t("replenishment.negatives.cleanup", "Open the full cleanup")}
          <ArrowRight className="size-4" />
        </Button>
      </div>
      <DataTable<NegativeStock>
        data={rows}
        columns={cols}
        getRowId={(row) => `${String(row.part_id)}-${String(row.warehouse_id)}`}
        stateKey="/purchasing/replenishment/negatives"
        loading={negatives.isLoading}
        rowActions={(row) => <OpenRecord target={{ kind: "part", id: row.part_id, tab: "stock" }} label={row.part_number} />}
        emptyMessage={t("replenishment.negatives.none", "Nothing below zero")}
      />
    </div>
  );
}

function FixTile({ active, label, count, onClick }: { active: boolean; label: string; count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex min-h-14 flex-col items-start justify-center rounded-lg border px-3 py-2 text-left transition",
        active ? "border-accent bg-accent-soft" : "border-line-soft bg-surface hover:border-line",
      )}
    >
      <span className="text-[11px] font-medium uppercase tracking-wider text-ink-faint">{label}</span>
      <span className="text-[18px] font-semibold tabular-nums text-ink">{formatWhole(count)}</span>
    </button>
  );
}
